import React, { Component, Suspense } from "react";
import type { BaseStormcloudPlayerProps } from "./props";
import { defaultProps } from "./props";
import { omit } from "./utils";
import players from "./players";
import type { PlayerConfig } from "./players";
import Player from "./Player.js";

const IS_BROWSER = typeof window !== "undefined" && window.document;
const IS_GLOBAL =
  typeof globalThis !== "undefined" &&
  globalThis.window &&
  globalThis.window.document;
const UniversalSuspense = IS_BROWSER || IS_GLOBAL ? Suspense : () => null;

const SUPPORTED_PROPS = [
  "src",
  "playing",
  "loop",
  "controls",
  "volume",
  "muted",
  "playbackRate",
  "width",
  "height",
  "style",
  "progressInterval",
  "playsInline",
  "autoplay",
  "preload",
  "poster",
  "className",
  "wrapperClassName",
  "wrapperStyle",
  "allowNativeHls",
  "lowLatencyMode",
  "isLiveStream",
  "driftToleranceMs",
  "immediateManifestAds",
  "debugAdTiming",
  "showCustomControls",
  "licenseKey",
  "isVmap",
  "vmapUrl",
  "adFailsafeTimeoutMs",
  "minSegmentsBeforePlay",
  "ctvAdRequest",
  "onReady",
  "onStart",
  "onPlay",
  "onPause",
  "onBuffer",
  "onBufferEnd",
  "onEnded",
  "onError",
  "onDuration",
  "onSeek",
  "onProgress",
  "onVolumeToggle",
  "onFullscreenToggle",
  "onControlClick",
];

export interface StormcloudPlayerProps extends BaseStormcloudPlayerProps {
  fallback?: React.ReactElement;
  wrapper?: React.ComponentType<{ children: React.ReactNode }> | string;
}

interface StormcloudPlayerState {
  showPreview: boolean;
}

const customPlayers: PlayerConfig[] = [];

export const createStormcloudPlayer = (
  playerList: PlayerConfig[],
  fallback?: PlayerConfig
) => {
  return class StormcloudPlayerClass extends Component<
    StormcloudPlayerProps,
    StormcloudPlayerState
  > {
    static displayName = "StormcloudPlayer";

    static defaultProps = {
      ...defaultProps,
      fallback: null,
      wrapper: "div",
    };

    static addCustomPlayer = (player: PlayerConfig) => {
      customPlayers.push(player);
    };

    static removeCustomPlayers = () => {
      customPlayers.length = 0;
    };

    static canPlay = (src: string): boolean => {
      for (const Player of [...customPlayers, ...playerList]) {
        if (Player.canPlay(src)) {
          return true;
        }
      }
      return false;
    };

    static canEnablePIP = (src: string): boolean => {
      for (const Player of [...customPlayers, ...playerList]) {
        if (Player.canEnablePIP && Player.canEnablePIP(src)) {
          return true;
        }
      }
      return false;
    };

    state: StormcloudPlayerState = {
      showPreview: false,
    };

    public wrapper?: HTMLElement;
    public player?: any;

    references = {
      wrapper: (wrapper: HTMLElement) => {
        this.wrapper = wrapper;
      },
      player: (player: any) => {
        this.player = player;
      },
    };

    getActivePlayer = (src?: string): PlayerConfig | null => {
      if (!src) return null;

      for (const player of [...customPlayers, ...playerList]) {
        if (player.canPlay(src)) {
          return player;
        }
      }

      if (fallback) {
        return fallback;
      }

      return null;
    };

    getAttributes = (src?: string) => {
      return omit(this.props, SUPPORTED_PROPS as any);
    };

    handleReady = () => {
      this.props.onReady?.(this as any);
    };

    seekTo = (
      fraction: number,
      type?: "seconds" | "fraction",
      keepPlaying?: boolean
    ) => {
      if (!this.player) return null;
      this.player.seekTo(fraction, type, keepPlaying);
    };

    getCurrentTime = (): number | null => {
      if (!this.player) return null;
      return this.player.getCurrentTime();
    };

    getSecondsLoaded = (): number | null => {
      if (!this.player) return null;
      return this.player.getSecondsLoaded();
    };

    getDuration = (): number | null => {
      if (!this.player) return null;
      return this.player.getDuration();
    };

    getInternalPlayer = (key = "player") => {
      if (!this.player) return null;
      return this.player.getInternalPlayer(key);
    };

    renderActivePlayer = (src?: string) => {
      if (!src) return null;

      const activePlayer = this.getActivePlayer(src);
      if (!activePlayer) return null;

      return React.createElement(Player, {
        ...this.props,
        key: activePlayer.key,
        ref: this.references.player,
        activePlayer: activePlayer.lazyPlayer || activePlayer,
        onReady: this.handleReady,
      });
    };

    render() {
      const {
        src,
        style,
        width,
        height,
        fallback: fallbackElement,
        wrapper: Wrapper,
      } = this.props;
      const attributes = this.getAttributes(src);
      const wrapperRef =
        typeof Wrapper === "string" ? this.references.wrapper : undefined;

      return React.createElement(
        Wrapper as any,
        {
          ref: wrapperRef,
          style: { ...style, width, height },
          ...attributes,
        },
        React.createElement(
          UniversalSuspense,
          { fallback: fallbackElement },
          this.renderActivePlayer(src)
        )
      );
    }
  };
};

const StormcloudPlayer = createStormcloudPlayer(
  players,
  players[players.length - 1]
);

export default StormcloudPlayer;
